// Validation utilities for onboarding data

export interface ValidationError {
  field: string;
  value: string;
  error: string;
  rowIndex: number;
  colIndex: number;
}

// Boolean columns that must be TRUE/FALSE/1/0
const BOOLEAN_COLUMNS = [
  "DaVinci Code Integration",
  "About Us Page",
  "Account Login Page",
  "Auctions Page",
  "Careers Page",
  "Insurance Info Page",
  "Moving Services",
  "Packing Supplies",
  "Pay By Phone",
  "Pay Online",
  "Recycle Program",
  "Referral Program",
  "Shred Program",
  "Size Guide",
  "Privacy Policy",
  "24 Hour Access",
  "After Hours Access",
  "Appointments",
  "Climate Controlled Units",
  "Conference Room",
  "Deliveries Accepted",
  "Drive-Up Units",
  "Dollies",
  "Dumpster",
  "Electricity In Unit",
  "Elevators",
  "Heated Units",
  "Moving Truck",
  "Notary",
  "Office Space",
  "Padlock Codes",
  "Video Surveillance",
  "Vehicle Storage",
  "Small Vehicle",
  "Indoor Vehicle",
  "Outdoor Vehicle",
  "Covered Vehicle",
  "RV",
  "Covered RV",
  "Enclosed RV",
  "Trailer",
  "Business Storage",
  "Lockers",
  "Wine",
  "Boat"
];

// Invalid entries to flag
const INVALID_ENTRIES = ["none", "n/a", "tbd", "no", "null", "undefined", ""];

export const validateLocationName = (value: string, allRecords: any[], currentIndex: number): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: false, error: "Location name is required" };
  }

  const duplicates = allRecords.filter(
    (record, index) =>
      index !== currentIndex && record["Location Name"] && record["Location Name"].toLowerCase().trim() === value.toLowerCase().trim()
  );

  if (duplicates.length > 0) {
    return { isValid: false, error: "Location name must be unique" };
  }

  return { isValid: true };
};

export const validateLocationId = (value: string, allRecords: any[], currentIndex: number): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: false, error: "Location ID is required" };
  }

  if (isNaN(parseInt(value))) {
    return { isValid: false, error: "Location ID must be a number" };
  }

  const duplicates = allRecords.filter(
    (record, index) => index !== currentIndex && record["Location ID"] && record["Location ID"].toString().trim() === value.toString().trim()
  );

  if (duplicates.length > 0) {
    return { isValid: false, error: "Location ID must be unique" };
  }

  return { isValid: true };
};

export const validateState = (value: string): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: true }; // Optional field
  }

  if (value.length !== 2) {
    return { isValid: false, error: "State must be exactly 2 characters" };
  }

  return { isValid: true };
};

export const validatePostalCode = (value: string, country: string): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: true }; // Optional field
  }

  const trimmedValue = value.toString().trim();

  // Default to US if country is not specified or empty
  const effectiveCountry = country && country.trim() !== "" ? country : "US";

  if (effectiveCountry === "US") {
    if (!/^\d{5}$/.test(trimmedValue)) {
      return { isValid: false, error: "US postal code must be 5 digits" };
    }
  } else if (effectiveCountry === "CA") {
    if (!/^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(trimmedValue)) {
      return { isValid: false, error: "Canadian postal code must be in A1A 1A1 format" };
    }
  }

  return { isValid: true };
};

export const validateCountry = (value: string): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: true }; // Optional field
  }

  if (value.length !== 2) {
    return { isValid: false, error: "Country must be exactly 2 characters" };
  }

  return { isValid: true };
};

export const validateURL = (value: string): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: true }; // Optional field
  }

  const trimmedValue = value.trim();

  // Check for trailing slashes
  if (trimmedValue.endsWith("/")) {
    return { isValid: false, error: "URL should not have trailing slash" };
  }

  // Check for query parameters
  if (trimmedValue.includes("?") || trimmedValue.includes("#")) {
    return { isValid: false, error: "URL should not contain query parameters" };
  }

  // Basic URL validation
  if (!trimmedValue.startsWith("http://") && !trimmedValue.startsWith("https://") && !trimmedValue.startsWith("/")) {
    return { isValid: false, error: "Must be a valid URL" };
  }

  return { isValid: true };
};

export const validateNumeric = (value: string, fieldName: string): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: true }; // Optional field
  }

  if (isNaN(parseInt(value))) {
    return { isValid: false, error: `${fieldName} must be a number` };
  }

  return { isValid: true };
};

export const validateBoolean = (value: string): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: true }; // Optional field
  }

  const normalizedValue = value.toString().toUpperCase();
  if (!["TRUE", "FALSE", "1", "0"].includes(normalizedValue)) {
    return { isValid: false, error: "Must be TRUE, FALSE, 1, or 0" };
  }

  return { isValid: true };
};

export const validateInvalidEntry = (value: string): { isValid: boolean; error?: string } => {
  if (!value) {
    return { isValid: true };
  }

  const normalizedValue = value.toString().toLowerCase().trim();
  if (INVALID_ENTRIES.includes(normalizedValue)) {
    return { isValid: false, error: "Invalid entry - should be removed or replaced" };
  }

  return { isValid: true };
};

export const validateUniqueColumn = (
  value: string,
  fieldName: string,
  allRecords: any[],
  currentIndex: number
): { isValid: boolean; error?: string } => {
  if (!value || value.trim() === "") {
    return { isValid: true }; // Optional field
  }

  const duplicates = allRecords.filter(
    (record, index) => index !== currentIndex && record[fieldName] && record[fieldName].toLowerCase().trim() === value.toLowerCase().trim()
  );

  if (duplicates.length > 0) {
    return { isValid: false, error: `${fieldName} must be unique` };
  }

  return { isValid: true };
};

// Main validation function
export const validateRecord = (record: Record<string, string>, allRecords: any[], currentIndex: number, headers: string[]): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Location Name validation
  const locationNameResult = validateLocationName(record["Location Name"], allRecords, currentIndex);
  if (!locationNameResult.isValid) {
    errors.push({
      field: "Location Name",
      value: record["Location Name"],
      error: locationNameResult.error!,
      rowIndex: currentIndex + 2, // +2 for header row and 1-based indexing
      colIndex: headers.indexOf("Location Name")
    });
  }

  // Location ID validation
  const locationIdResult = validateLocationId(record["Location ID"], allRecords, currentIndex);
  if (!locationIdResult.isValid) {
    errors.push({
      field: "Location ID",
      value: record["Location ID"],
      error: locationIdResult.error!,
      rowIndex: currentIndex + 2,
      colIndex: headers.indexOf("Location ID")
    });
  }

  // State validation
  const stateResult = validateState(record["State"]);
  if (!stateResult.isValid) {
    errors.push({
      field: "State",
      value: record["State"],
      error: stateResult.error!,
      rowIndex: currentIndex + 2,
      colIndex: headers.indexOf("State")
    });
  }

  // Postal Code validation
  const postalCodeResult = validatePostalCode(record["Postal Code"], record["Country"]);
  if (!postalCodeResult.isValid) {
    errors.push({
      field: "Postal Code",
      value: record["Postal Code"],
      error: postalCodeResult.error!,
      rowIndex: currentIndex + 2,
      colIndex: headers.indexOf("Postal Code")
    });
  }

  // Country validation
  const countryResult = validateCountry(record["Country"]);
  if (!countryResult.isValid) {
    errors.push({
      field: "Country",
      value: record["Country"],
      error: countryResult.error!,
      rowIndex: currentIndex + 2,
      colIndex: headers.indexOf("Country")
    });
  }

  // URL validation
  headers.forEach((header, colIndex) => {
    if (header.toLowerCase().includes("url")) {
      const urlResult = validateURL(record[header]);
      if (!urlResult.isValid) {
        errors.push({
          field: header,
          value: record[header],
          error: urlResult.error!,
          rowIndex: currentIndex + 2,
          colIndex
        });
      }
    }
  });

  // FMS Integration ID validation
  const fmsColumnName = headers.includes("FMS Integration ID")
    ? "FMS Integration ID"
    : headers.includes("FMS Integration")
      ? "FMS Integration"
      : null;
  if (fmsColumnName) {
    const fmsResult = validateNumeric(record[fmsColumnName], fmsColumnName);
    if (!fmsResult.isValid) {
      errors.push({
        field: fmsColumnName,
        value: record[fmsColumnName],
        error:
          "Must be a numeric ID. To find it: go to https://v3.swivl.studio/settings/fms-integrations, open the integration, and use the number from the URL - i.e. 656 from https://v3.swivl.studio/fms/656",
        rowIndex: currentIndex + 2,
        colIndex: headers.indexOf(fmsColumnName)
      });
    }
  }

  // Brand validation
  if (headers.includes("Brand")) {
    const brandResult = validateNumeric(record["Brand"], "Brand");
    if (!brandResult.isValid) {
      errors.push({
        field: "Brand",
        value: record["Brand"],
        error: brandResult.error!,
        rowIndex: currentIndex + 2,
        colIndex: headers.indexOf("Brand")
      });
    }
  }

  // Boolean columns validation
  BOOLEAN_COLUMNS.forEach((column) => {
    if (headers.includes(column)) {
      const booleanResult = validateBoolean(record[column]);
      if (!booleanResult.isValid) {
        errors.push({
          field: column,
          value: record[column],
          error: booleanResult.error!,
          rowIndex: currentIndex + 2,
          colIndex: headers.indexOf(column)
        });
      }
    }
  });

  // QR Link Slug uniqueness validation
  const qrSlugColumns = ["QR Link Slug", "QR Link Short Slug"];
  qrSlugColumns.forEach((column) => {
    if (headers.includes(column)) {
      const uniqueResult = validateUniqueColumn(record[column], column, allRecords, currentIndex);
      if (!uniqueResult.isValid) {
        errors.push({
          field: column,
          value: record[column],
          error: uniqueResult.error!,
          rowIndex: currentIndex + 2,
          colIndex: headers.indexOf(column)
        });
      }
    }
  });

  // Invalid entry validation for all fields
  headers.forEach((header, colIndex) => {
    const invalidEntryResult = validateInvalidEntry(record[header]);
    if (!invalidEntryResult.isValid) {
      errors.push({
        field: header,
        value: record[header],
        error: invalidEntryResult.error!,
        rowIndex: currentIndex + 2,
        colIndex
      });
    }
  });

  return errors;
};
